import React, { useContext, useEffect, useState } from 'react';
import { Vehicle } from '@esportal/portal-service/src/models/vehicles';
import {
	Container,
	TextAlignment,
	TokenTextAppearance,
	Text,
	ContainerPadding,
	Select,
	Layout,
	Breakpoints,
	styled,
	ContainerGutter,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { ServicesCitaTaller } from './components/services-cita-taller';
import { ServicesHeader } from './components/services-header';
import { isLongdriveActive, isWarrantyActive, isIDcareActive, getFilteredVehicles } from '../../helpers/servicesHelper';
import { ServicesCoberturas } from './components/services-cobertura';
import { ServicesDealer } from './components/services-dealer';
import { ServiceGroupType } from './models/services-layer-types';
import { PortalContext } from '../../context/portal-context';
import { COLOR_TOKEN } from '../../helpers/color-tokens';
import { GetPersonUser } from '@esportal/portal-service/src/models/portal';
import { useMyServicesState } from './hooks/use-my-services-state';
import { useMyServicesLayer } from './hooks/use-my-services-layer';

const BackgroundPrimary = styled.div`
	background-color: ${COLOR_TOKEN.background.secondary};
`;

const BackgroundWrapper = styled.div`
	position: relative;
	width: 100%;
`;

const StyledSelect = styled(Select)`
	position: relative;
	z-index: 0;

	@media (max-width: 560px) {
		width: 180px;
	}

	@media (min-width: 561px) {
		min-width: 240px;
	}
`;

const HeaderContainer = styled(Container)`
	@media (max-width: 560px) {
		& div {
			display: block;
		}
	}
`;

const useIsMobile = (maxWidth = 560): boolean => {
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const mediaQuery = window.matchMedia(`(max-width: ${maxWidth}px)`);
		const handleChange = () => setIsMobile(mediaQuery.matches);

		handleChange();
		mediaQuery.addEventListener('change', handleChange);

		return () => mediaQuery.removeEventListener('change', handleChange);
	}, [maxWidth]);

	return isMobile;
};

export const MyServices = ({
	vehicles,
	userData,
}: {
	vehicles: Vehicle[];
	userData: GetPersonUser;
}): React.ReactElement => {
	const portalContext = useContext(PortalContext);

	const filteredVehicles = getFilteredVehicles(vehicles);

	if (filteredVehicles.length < 1) return <></>;

	const { selectedVehicle, handleSelectedVehicleChange, handleAccordionToggle, isAccordionOpen, isPreviousOpen } =
		useMyServicesState({ filteredVehicles, userData });

	const { handleLayerOpen } = useMyServicesLayer({ vehicle: selectedVehicle, userData });

	const isMobile = useIsMobile();

	return (
		<Container wrap={'always'}>
			<Container stretchContent>
				<BackgroundWrapper data-testid="myServices-background">
					<Layout
						data-testid="myServices-layout"
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b560]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b1280]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b1920]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
						}}
						allowOverflowingContent
					>
						<HeaderContainer stretchContent data-testid="HeaderContainer">
							<Container
								padding={{
									top: ContainerPadding.dynamic0130,
								}}
								verticalAlign={'center'}
							>
								<Container data-testid="servicios-section-title" wrap="always" gutter={ContainerGutter.dynamic0020}>
									<Text
										appearance={TokenTextAppearance.bigcopy200}
										color={TokenTextColor.primary}
										textAlign={TextAlignment.left}
										bold
									>
										Servicios
									</Text>
									<Text
										appearance={TokenTextAppearance.headline500}
										color={TokenTextColor.primary}
										textAlign={TextAlignment.left}
									>
										Lo mejor <b>para tu coche</b>
									</Text>
								</Container>
							</Container>

							{vehicles.length > 1 && (
								<Container
									padding={{
										top: {
											[Breakpoints.default]: ContainerPadding.dynamic0250,
											[Breakpoints.b560]: ContainerPadding.dynamic0150,
										},
										bottom: ContainerPadding.dynamic0100,
										right: ContainerPadding.static100,
										left: ContainerPadding.static100,
									}}
									horizontalAlign={isMobile ? 'flex-start' : 'flex-end'}
									verticalAlign={'center'}
								>
									<StyledSelect
										label="Cambiar de coche"
										defaultValue={selectedVehicle.name}
										onChange={handleSelectedVehicleChange}
										isFloating
										data-testid="Select!"
									>
										{filteredVehicles.map((vehicle: Vehicle) => (
											<option key={vehicle.name} value={vehicle.name}>
												{vehicle.vgedModelGroup}
											</option>
										))}
									</StyledSelect>
								</Container>
							)}
						</HeaderContainer>
					</Layout>

					<ServicesHeader vehicle={selectedVehicle} />
				</BackgroundWrapper>
			</Container>

			<BackgroundPrimary>
				<Container
					stretchContent
					padding={{
						bottom: ContainerPadding.dynamic0100,
					}}
				>
					<Layout
						data-testid="myServices-layout"
						appearance={{
							[Breakpoints.default]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b560]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b1280]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
							[Breakpoints.b1920]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 22 },
								{ name: '.', columns: 1 },
							],
						}}
						allowOverflowingContent
					>
						{(isLongdriveActive(selectedVehicle) ||
							isWarrantyActive(selectedVehicle) ||
							isIDcareActive(selectedVehicle)) && (
							<ServicesCoberturas
								vehicle={selectedVehicle}
								isOpen={isAccordionOpen(ServiceGroupType.Coberturas)}
								isPreviousOpen={isPreviousOpen(ServiceGroupType.Coberturas)}
								onToggle={() => handleAccordionToggle(ServiceGroupType.Coberturas)}
								openServicesLayer={handleLayerOpen}
							/>
						)}

						{userData.portalUser.data.dealers?.pv?.commercialName && (
							<ServicesDealer
								dealers={userData.portalUser.data.dealers.pv}
								isOpen={isAccordionOpen(ServiceGroupType.Dealer)}
								isPreviousOpen={isPreviousOpen(ServiceGroupType.Dealer)}
								onToggle={() => handleAccordionToggle(ServiceGroupType.Dealer)}
								openServicesLayer={handleLayerOpen}
							/>
						)}

						<ServicesCitaTaller
							vehicleForm={selectedVehicle}
							dealers={userData.portalUser.data.dealers?.pv}
							portalContext={portalContext}
						/>
					</Layout>
				</Container>
			</BackgroundPrimary>
		</Container>
	);
};
